<template>
    <div class="login flex-center" :class="providerClasses">
        <v-card v-if="!recoverPassword" class="modal modal-round">
            <div class="logo-wrapper flex-center">
                <img :src="logoUrl" :class="{ 'logo-wifeed': usesNewLogo }" alt="wifeed-logo" class="logo" />
            </div>

            <form class="form" @submit.prevent="login">
                <v-text-field
                    v-model.trim="email"
                    :label="$t('message.login.message.email')"
                    class="font-lg"
                    id="emailInput"
                ></v-text-field>

                <v-text-field
                    v-model.trim="password"
                    :label="$t('message.login.message.password')"
                    type="password"
                    id="current-password"
                    autocomplete="current-password"
                    aria-label="password"
                    class="font-lg"
                ></v-text-field>

                <vue-recaptcha
                    @verify="onVerifyCaptcha"
                    ref="recaptcha"
                    size="invisible"
                    :loadRecaptchaScript="true"
                    :sitekey="recaptchaKey"
                >
                </vue-recaptcha>

                <button type="submit" class="btn btn-primary btn-extra-round">
                    {{ $t("message.login.message.access") }}
                </button>
                <div
                    v-if="!isTrial"
                    class="forgot-password"
                    @click="forgotPassword"
                >
                    {{ $t("message.login.message.forgotPassword") }}
                </div>

                <LocaleChangerLogin />
            </form>
        </v-card>

        <SetEmailRecoverPasswordCard
            v-if="recoverPassword"
            @back="backToLogin"
        />
    </div>
</template>

<script>
import SetEmailRecoverPasswordCard from "@views/login/components/SetEmailRecoverPasswordCard.vue";
import LocaleChangerLogin from "@views/login/components/LocaleChangerLogin";
import EventBus from "@packages/event-bus";
import VueRecaptcha from "vue-recaptcha";
import { i18n } from "@packages/I18n";
import jwtDecode from "jwt-decode";
import provider from "@mixins/provider";

export default {
    metaInfo: {
        title: "- Login",
        meta: [
            {
                vmid: "app",
                name: "description",
                content: i18n.t("message.login.message.metaDescription"),
            },
        ],
    },
    components: {
        "vue-recaptcha": VueRecaptcha,
        SetEmailRecoverPasswordCard,
        LocaleChangerLogin,
    },
    data() {
        return {
            email: null,
            password: null,
            recoverPassword: false,
            recaptchaKey: "6LfD48YUAAAAAPJ2LiZ86AloaAMg5Yq9ZZQyo69-",
        };
    },

    mixins: [provider],

    computed: {
        user() {
            return this.$store.state.users.user;
        },

        admin() {
            return this.$store.state.users.admin;
        },

        isTrial() {
            return (
                process.env.NODE_ENV === "trial" ||
                window.location.hostname === "trial.wifeed.com.br"
            );
        },
    },

    methods: {
        backToLogin() {
            this.recoverPassword = false;
        },
        onVerifyCaptcha: function (response) {
            this.submitLogin(response);
        },
        async login() {
            if (!this.email) {
                EventBus.$emit("show-message", {
                    type: "ERROR",
                    message: "Informe o e-mail",
                });
                return;
            }
            if (!this.password) {
                EventBus.$emit("show-message", {
                    type: "ERROR",
                    message: "Informe a senha",
                });
                return;
            }
            this.$refs.recaptcha.execute();
        },
        async forgotPassword() {
            this.recoverPassword = true;
        },
        async submitLogin(keycaptcha) {
            try {
                await this.$auth.login({
                    username: this.email,
                    password: this.password,
                    captcha: keycaptcha,
                });

                if (this.user) {
                    const resultToken = jwtDecode(this.$auth.token());

                    let parceiroBool =
                        resultToken.accountType === "PARCEIRO" ? true : false;

                    this.$store.commit("SET_PARCEIRO", parceiroBool);

                    this.enterApp(
                        parceiroBool ? "/estabelecimentos" : "/visao-geral"
                    );
                }

                if (this.admin) {
                    this.enterApp("/admin/estabelecimentos");
                }
            } catch (error) {
                console.log(error);
            }
        },
        enterApp(path) {
            window.location.assign(path);
        },

        handleAutofilledInputs() {
            const emailInput = document.querySelector("#emailInput");
            emailInput.addEventListener("animationstart", (e) => {
                if (e.animationName.includes("autofill")) {
                    const labels = Array.from(
                        document.getElementsByTagName("label")
                    );
                    labels.map((label) =>
                        label.classList.add("v-label--active")
                    );
                }
            });
        },
    },

    async mounted() {
        this.handleAutofilledInputs();

        this.setWhitelabelClassOnBody();

        if (this.user || this.admin) return this.$router.back();

        const email = process.env.VUE_APP_DEFAULT_USER;
        const password = process.env.VUE_APP_DEFAULT_PASSWORD;

        if (email) this.email = email;

        if (password) this.password = password;

        if (!this.$auth.token()) return;

        await this.$auth.fetchUserData();
        this.$router.back();
    },
};
</script>

<style lang="scss" scoped>
@import "~@variables";

@keyframes autofill {
    from {
        color: initial;
        background-color: initial;
    }
}

.login {
    width: 100vw;
    height: 100vh;
    background: $primary;

    .modal {
        min-width: 500px;
        padding: 42px;
        flex-direction: column;

        .logo-wrapper {
            width: 100%;
            margin-bottom: 42px;

            .logo {
                height: 92px;

                &.logo-wifeed {
                    max-width: 197px;
                    height: auto;
                }
            }
        }

        .form {
            text-align: center;

            & > *:not(:last-child) {
                margin-bottom: 22px !important;
            }

            .btn {
                font-size: 18px;
                text-transform: uppercase;
                padding: 8px 42px;
            }

            .v-text-field {
                ::v-deep input {
                    &:-webkit-autofill {
                        animation-name: autofill;
                        transition: background-color 50000s ease-in-out 0s;
                    }

                    &:not(:-webkit-autofill) {
                        animation-name: onAutoFillCancel;
                    }
                }
            }
        }

        select {
            position: inherit;
        }
    }

    .forgot-password {
        cursor: pointer;
    }

    &.login_custom-provider {
        .modal {
            flex-direction: column;
            min-width: 500px;
            padding: 42px;

            .logo-wrapper {
                width: 100%;
                margin-bottom: 42px;

                .logo {
                    height: 73px;
                }
            }
        }
    }

    &.login-wcs {
        background: rgb(255, 153, 0);
        background: linear-gradient(
            90deg,
            rgba(255, 153, 0, 1) 0%,
            rgba(243, 146, 0, 1) 50%,
            rgba(213, 118, 0, 1) 100%
        );
    }

    &.login-haggi {
        background: #2ebeb1;
    }

    &.login-tely {
        background: #0048fd;
    }

    &.login-ntcorp {
        background: #2e3b56;
    }

    &.login-data-carrier {
        background: #365176;

        .logo {
            background-color: #365176;
            padding: 10px;
            border-radius: 5px;
        }
    }

    &.login-vivo {
        background: #660099;

        .logo {
            width: 180px;
            height: 102px !important;
        }
    }

    &.login-vero {
        background: #29377f;
    }

    &.login-abc {
        background: #5f1319;
    }

    &.login-unifique {
        background: #212492;
    }

    &.login-master {
        background: #0031CF;
    }
}
</style>
